import { getSlugFromPage } from '../../utils/routeUtils';

const FACEBOOK_APP_ID = import.meta.env.VITE_FACEBOOK_APP_ID || '';
/** Obrigatório em app tipo Empresa (Facebook Login for Business). */
const FACEBOOK_LOGIN_CONFIG_ID = import.meta.env.VITE_FACEBOOK_LOGIN_CONFIG_ID || '';

/**
 * Scopes alinhados à Login Configuration (VITE_FACEBOOK_LOGIN_CONFIG_ID).
 * Com User access token a Meta permite scope + config_id; sem scope alguns apps
 * caem em "supported permission" mesmo com config válida.
 *
 * Nunca peça scope que não esteja na Login Configuration / App Dashboard —
 * a Meta responde "Invalid Scopes" e bloqueia o diálogo (visível a developers).
 * CAPI Click-to-IG (`instagram_manage_events`) só entra aqui depois de estar
 * na mesma configuração + Advanced Access; até lá o dataset IG falha em soft.
 */
const FACEBOOK_SCOPES = [
    'public_profile',
    'email',
    'ads_management',
    'business_management',
    'leads_retrieval',
    'pages_show_list',
    'pages_read_engagement',
].join(',');

const META_OAUTH_STATE_KEY = 'adlitics_meta_oauth_state';
const META_OAUTH_ORG_KEY = 'adlitics_meta_oauth_org_id';
const META_CONNECTING_KEY = 'adlitics_meta_connecting';
/** Sobrevive remount / WebView do Facebook que perde sessionStorage. */
const META_PENDING_CODE_KEY = 'adlitics_meta_oauth_pending_code';

const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Indica que o fluxo seguiu por redirect e a página está navegando para o Meta. */
export class MetaOAuthRedirectPending extends Error {
    constructor() {
        super('Redirecionando para autenticação Meta');
        this.name = 'MetaOAuthRedirectPending';
    }
}

export type MetaOAuthCallback = {
    code: string;
    organizationId: string;
};

export const getMetaOAuthRedirectUri = (): string => {
    const slug = getSlugFromPage('tracking');
    return `${window.location.origin}/${slug}`;
};

const requireMetaOAuthEnv = (): { appId: string; configId: string } => {
    if (!FACEBOOK_APP_ID) {
        throw new Error('App do Facebook não configurado (VITE_FACEBOOK_APP_ID).');
    }
    if (!FACEBOOK_LOGIN_CONFIG_ID) {
        throw new Error(
            'Configure VITE_FACEBOOK_LOGIN_CONFIG_ID: Meta → Login do Facebook para Empresas → Configurações → criar configuração (User access token) com ads_management, business_management, leads_retrieval, pages_show_list, pages_read_engagement.'
        );
    }
    return { appId: FACEBOOK_APP_ID, configId: FACEBOOK_LOGIN_CONFIG_ID };
};

/** state = nonce.organizationId — org volta na URL mesmo se o storage sumir no retorno do Facebook. */
const buildOAuthState = (organizationId: string): { state: string; nonce: string } => {
    const nonce = crypto.randomUUID();
    return { nonce, state: `${nonce}.${organizationId}` };
};

const parseOAuthState = (state: string): { nonce: string; organizationId: string } | null => {
    const dot = state.indexOf('.');
    if (dot <= 0) return null;
    const nonce = state.slice(0, dot);
    const organizationId = state.slice(dot + 1);
    if (!UUID_RE.test(nonce) || !UUID_RE.test(organizationId)) return null;
    return { nonce, organizationId };
};

/** state Meta = nonce.orgId (UUIDs). Usado pelos gates sem depender de sessionStorage. */
export const isMetaOAuthStateParam = (state: string | null | undefined): boolean =>
    Boolean(state && parseOAuthState(state));

const writePendingCallback = (pending: MetaOAuthCallback): void => {
    const raw = JSON.stringify(pending);
    sessionStorage.setItem(META_PENDING_CODE_KEY, raw);
    sessionStorage.setItem(META_CONNECTING_KEY, 'true');
    try {
        localStorage.setItem(META_PENDING_CODE_KEY, raw);
        localStorage.setItem(META_CONNECTING_KEY, 'true');
    } catch {
        /* private mode */
    }
};

const readPendingCallback = (): MetaOAuthCallback | null => {
    const raw =
        sessionStorage.getItem(META_PENDING_CODE_KEY) ||
        localStorage.getItem(META_PENDING_CODE_KEY);
    if (!raw) return null;
    try {
        const pending = JSON.parse(raw) as MetaOAuthCallback;
        if (pending.code && pending.organizationId) return pending;
    } catch {
        sessionStorage.removeItem(META_PENDING_CODE_KEY);
        try {
            localStorage.removeItem(META_PENDING_CODE_KEY);
        } catch {
            /* private mode */
        }
    }
    return null;
};

/**
 * Persiste ?code=&state= Meta assim que a URL aparece — antes de gates de
 * onboarding/subscription limparem a query (Facebook WebView apaga sessionStorage).
 */
export const persistMetaOAuthCallbackFromUrl = (): boolean => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('error')) return false;

    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state || state === 'adlitics_whatsapp') return false;

    const path = window.location.pathname.replace(/\/$/, '') || '/';
    const igSlug = getSlugFromPage('connections');
    if (path === `/${igSlug}` || path.endsWith(`/${igSlug}`)) return false;

    // Google usa state = UUID puro. Meta usa nonce.orgId — só ceder se for o state do Google.
    const googleState = sessionStorage.getItem('adlitics_google_ads_oauth_state');
    if (googleState && state === googleState) return false;

    const parsed = parseOAuthState(state);
    if (!parsed) return false;

    persistOAuthStart(parsed.nonce, parsed.organizationId);
    writePendingCallback({ code, organizationId: parsed.organizationId });
    return true;
};

const persistOAuthStart = (nonce: string, organizationId: string): void => {
    try {
        localStorage.setItem(META_OAUTH_STATE_KEY, nonce);
        localStorage.setItem(META_OAUTH_ORG_KEY, organizationId);
    } catch {
        /* private mode */
    }
    sessionStorage.setItem(META_OAUTH_STATE_KEY, nonce);
    sessionStorage.setItem(META_OAUTH_ORG_KEY, organizationId);
    sessionStorage.setItem(META_CONNECTING_KEY, 'true');
};

const readStoredNonce = (): string | null =>
    sessionStorage.getItem(META_OAUTH_STATE_KEY) || localStorage.getItem(META_OAUTH_STATE_KEY);

/**
 * OAuth redirect via Login for Business (config_id + scope para User access token).
 */
export const startFacebookOAuthRedirect = (organizationId: string): void => {
    const { appId, configId } = requireMetaOAuthEnv();
    const redirectUri = getMetaOAuthRedirectUri();
    const { state, nonce } = buildOAuthState(organizationId);

    sessionStorage.removeItem(META_PENDING_CODE_KEY);
    // Evita callback Meta ser ignorado por OAuth Google abandonado na mesma rota.
    sessionStorage.removeItem('adlitics_google_ads_oauth_state');
    sessionStorage.removeItem('adlitics_google_ads_oauth_org_id');
    sessionStorage.removeItem('adlitics_google_ads_oauth_pending_code');
    persistOAuthStart(nonce, organizationId);

    const params = new URLSearchParams({
        client_id: appId,
        redirect_uri: redirectUri,
        config_id: configId,
        scope: FACEBOOK_SCOPES,
        response_type: 'code',
        state,
    });

    window.location.assign(`https://www.facebook.com/v21.0/dialog/oauth?${params.toString()}`);
};

function mapMetaOAuthError(error: string, description?: string | null): Error {
    const text = (description || '').toLowerCase();

    if (error === 'access_denied') {
        return new Error('Autorização cancelada. Tente conectar novamente e aceite todas as permissões.');
    }
    if (error === 'redirect_uri_mismatch' || text.includes('redirect') || text.includes('bloqueada')) {
        return new Error(
            `URL de retorno não autorizada: ${getMetaOAuthRedirectUri()}. Cadastre exatamente esta URI no Meta → Login do Facebook → URIs de redirecionamento OAuth válidos.`
        );
    }
    if (text.includes('supported permission')) {
        return new Error(
            'Login for Business sem configuração válida. Crie uma Login Configuration no Meta (User access token + permissões de Ads/Pages) e defina VITE_FACEBOOK_LOGIN_CONFIG_ID no Cloudflare + redeploy.'
        );
    }
    if (text.includes('invalid scope') || text.includes('invalid scopes')) {
        return new Error(
            'Scope Meta inválido ou fora da Login Configuration. Peça só permissões cadastradas na mesma config_id (Ads/Pages).'
        );
    }
    if (
        error === 'app_not_active' ||
        text.includes('development') ||
        text.includes('desenvolvimento') ||
        text.includes('not available') ||
        text.includes('não está disponível') ||
        text.includes("isn't available") ||
        text.includes('isnt available')
    ) {
        return new Error(
            'OAuth Meta indisponível. Confirme app em modo Live e Advanced Access (public_profile, ads_management, business_management, leads_retrieval, pages_show_list, pages_read_engagement).'
        );
    }
    if (text.includes('permission') || text.includes('permiss')) {
        return new Error(
            'Permissão Meta negada ou sem Advanced Access. Confirme App Review das permissões de Ads/Pages e app em modo Live.'
        );
    }

    return new Error(description || 'Falha na autorização com o Meta.');
}

export const clearMetaOAuthUrlParams = (): void => {
    const url = new URL(window.location.href);
    ['code', 'state', 'error', 'error_reason', 'error_description'].forEach((key) => {
        url.searchParams.delete(key);
    });
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
};

export const clearMetaOAuthSession = (): void => {
    sessionStorage.removeItem(META_OAUTH_STATE_KEY);
    sessionStorage.removeItem(META_OAUTH_ORG_KEY);
    sessionStorage.removeItem(META_CONNECTING_KEY);
    sessionStorage.removeItem(META_PENDING_CODE_KEY);
    try {
        localStorage.removeItem(META_OAUTH_STATE_KEY);
        localStorage.removeItem(META_OAUTH_ORG_KEY);
        localStorage.removeItem(META_CONNECTING_KEY);
        localStorage.removeItem(META_PENDING_CODE_KEY);
    } catch {
        /* private mode */
    }
};

export const readMetaOAuthCallback = (): MetaOAuthCallback | null => {
    const fromStorage = readPendingCallback();
    if (fromStorage) return fromStorage;

    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');

    if (error) {
        throw mapMetaOAuthError(error, params.get('error_description') || params.get('error_reason'));
    }

    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state) return null;
    if (state === 'adlitics_whatsapp') return null;

    // Instagram retorna em /canais-de-atendimento — WebView do Facebook costuma apagar storage
    const path = window.location.pathname.replace(/\/$/, '') || '/';
    const igSlug = getSlugFromPage('connections');
    if (path === `/${igSlug}` || path.endsWith(`/${igSlug}`)) {
        return null;
    }

    // Mesma rota (/integracoes) serve Meta e Google.
    // Google: state = UUID puro salvo em sessionStorage.
    // Meta: state = nonce.orgId — nunca engolir Meta por flag Google/Instagram stale.
    const googleState = sessionStorage.getItem('adlitics_google_ads_oauth_state');
    if (googleState && state === googleState) {
        return null;
    }

    const parsed = parseOAuthState(state);
    const savedNonce = readStoredNonce();

    // Formato novo: nonce.orgId no state. Confiar no state da URL (igual Instagram):
    // localStorage pode ter nonce velho de tentativa anterior / outra aba —
    // isso gerava "Sessão de login inválida" com OAuth Meta já aprovado.
    if (parsed) {
        if (savedNonce !== parsed.nonce) {
            persistOAuthStart(parsed.nonce, parsed.organizationId);
        }
        const pending = { code, organizationId: parsed.organizationId };
        writePendingCallback(pending);
        return pending;
    }

    // Formato legado: state = uuid puro + org no storage
    if (!savedNonce || state !== savedNonce) {
        clearMetaOAuthUrlParams();
        throw new Error('Sessão de login inválida. Inicie a conexão novamente.');
    }

    const organizationId =
        sessionStorage.getItem(META_OAUTH_ORG_KEY) || localStorage.getItem(META_OAUTH_ORG_KEY);
    if (!organizationId) {
        clearMetaOAuthUrlParams();
        throw new Error('Organização não encontrada. Inicie a conexão novamente.');
    }

    const pending = { code, organizationId };
    writePendingCallback(pending);
    return pending;
};
